import { useCallback, useEffect, useMemo, useState } from "react";

import { PlatformService } from "@/services/platformService";

/**
 * What the version numbers on this screen mean.
 *
 * Two payloads, both read from code rather than from any database: the RLS
 * version catalogue and the Alembic revision graph. Neither changes while the
 * page is open, and both are the same for every database, so they are fetched
 * once here and every surface that needs them reads from this one copy — the
 * row chips, the two drawers, both rollback confirmations and the upgrade
 * modal. Fetching them per component would put the same two requests behind
 * every click.
 *
 * A failure is deliberately not fatal. The fleet table is the part of this
 * screen that must always work; if the descriptions cannot be loaded the
 * lookups return undefined, callers fall back to the bare number they showed
 * before, and `error` lets the page say so quietly.
 */
export const useVersionCatalog = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rlsCatalog, setRlsCatalog] = useState([]);
    const [rlsCurrent, setRlsCurrent] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [head, setHead] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        // Each half is useful without the other, so they settle independently:
        // a broken revision graph must not blank the RLS descriptions too.
        const [rls, alembic] = await Promise.all([
            PlatformService.fetchRlsVersions().catch((e) => {
                console.warn("RLS version catalog fetch failed:", e);
                return null;
            }),
            PlatformService.fetchAlembicRevisions().catch((e) => {
                console.warn("Alembic revision list fetch failed:", e);
                return null;
            }),
        ]);

        setRlsCatalog(rls?.catalog ?? []);
        setRlsCurrent(rls?.current ?? null);
        setRevisions(alembic?.revisions ?? []);
        setHead(alembic?.head ?? null);
        setError(
            rls === null && alembic === null
                ? "Version details could not be loaded."
                : null
        );
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const rlsByVersion = useMemo(() => {
        const map = new Map();
        for (const entry of rlsCatalog) map.set(entry.version, entry);
        return map;
    }, [rlsCatalog]);

    const revisionByHash = useMemo(() => {
        const map = new Map();
        for (const revision of revisions) map.set(revision.revision, revision);
        return map;
    }, [revisions]);

    /** One RLS version's description, or undefined if it is not in the archive. */
    const rlsVersion = useCallback(
        (version) => (version == null ? undefined : rlsByVersion.get(version)),
        [rlsByVersion]
    );

    /** One revision's description, or undefined if this build has never heard of it. */
    const revision = useCallback(
        (hash) => (hash ? revisionByHash.get(hash) : undefined),
        [revisionByHash]
    );

    /**
     * Descriptions for a list of revision hashes, in the order given.
     *
     * A hash with no description still comes back — as `{ revision, title:
     * null }` — because dropping it would make a pending list of five look
     * like a pending list of four.
     */
    const describeRevisions = useCallback(
        (hashes) =>
            (hashes ?? []).map(
                (hash) => revisionByHash.get(hash) ?? { revision: hash, title: null }
            ),
        [revisionByHash]
    );

    // Memoised because the page passes this whole object into a memoised column
    // definition and into several components. A fresh object every render would
    // rebuild the table's columns on every keystroke in the search box.
    return useMemo(
        () => ({
            loading,
            error,
            reload: load,
            // RLS
            rlsCatalog,
            rlsCurrent,
            rlsVersion,
            // Alembic
            revisions,
            head,
            revision,
            describeRevisions,
        }),
        [
            loading,
            error,
            load,
            rlsCatalog,
            rlsCurrent,
            rlsVersion,
            revisions,
            head,
            revision,
            describeRevisions,
        ]
    );
};

export default useVersionCatalog;
